import React from 'react';
import { useTranslation, Trans } from 'react-i18next';
import { toastr } from 'react-redux-toastr';
import { useDispatch, useSelector } from 'react-redux';
import { pendingTask, begin, end } from 'react-redux-spinner';
import { appliedUkgUserOffSetDateTimeNow, getAsLongDate } from '@commonClient2/services/ukgDateTime';
import { publishBid, cancelBid, refreshBidDashboard } from '../../store/bidProfileManagement/bidProfileManagement.middlewares';
import { openPublishProgressSidePanel } from '../../store/workplanPublishProgress';
import { LOADING_START, LOADING_END } from '../../../store/loading';
import * as bidProfileService from '../../services/api-endpoints/bidProfile.service';
import { ManagerBidAction, BidProfileEditTab, ScheduleSource } from '../../../Enums';

/**
 * @param {string} profileId
 * @param {boolean} isPublishingToWorkplan
 * @param {{
 *  bidProfiles: BidProfile.IBriefBaseProfile[],
 *  hyperfinds: ManagerAppData.IHyperfind[],
 *  managerBidSetups: ManagerAppData.IManagerBidSetup[],
 * }} storeData
*/
const useBidProfileSplitButton = (profileId, isPublishingToWorkplan, storeData) => {
    const dispatch = useDispatch();
    const { t: tPrompt } = useTranslation('shiftBidding', { keyPrefix: 'manager.bidProfileEdit.bidDashboard.prompt' });

    const { bidProfile } = useSelector((/** @type {ShiftBidding.ShiftBiddingStore} */ state) => state);

    const signOffOrCancel = async (/** @type {number} */ bidAction) => dispatch((bidAction === ManagerBidAction.Publish
        ? publishBid : cancelBid)(profileId, storeData));

    const executeAction = async (bidAction) => {        
        if (bidAction === ManagerBidAction.ForceStart) {
            try {
                dispatch({ type: LOADING_START, [pendingTask]: begin });
                await bidProfileService.activateBid(profileId);
                toastr.success(
                    tPrompt('success'),
                    tPrompt('forceStartBidSuccess'),
                );
                // Refresh the bid profile data after successful action
                dispatch(refreshBidDashboard(profileId, storeData, BidProfileEditTab.Dashboard));
            } catch (error) {
                toastr.error(
                    tPrompt('error'),
                    tPrompt('forceStartBidError'),
                );
                console.error('Force Start Bid error:', error);
            } finally {
                dispatch({ type: LOADING_END, [pendingTask]: end });
            }
        } else if (bidAction === ManagerBidAction.ForceClose) {
            try {
                dispatch({ type: LOADING_START, [pendingTask]: begin });
                await bidProfileService.closeBid(profileId);
                toastr.success(
                    tPrompt('success'),
                    tPrompt('forceCloseBidSuccess'),
                );
                // Refresh the bid profile data after successful action
                dispatch(refreshBidDashboard(profileId, storeData, BidProfileEditTab.Dashboard));
            } catch (error) {
                toastr.error(
                    tPrompt('error'),
                    tPrompt('forceCloseBidError'),
                );
                console.error('Force Close Bid error:', error);
            } finally {
                dispatch({ type: LOADING_END, [pendingTask]: end });
            }
        } else {
            return signOffOrCancel(bidAction);
        }
    };

    const getPromptKey = (bidAction) => {
        switch (bidAction) {
            case ManagerBidAction.Publish:
                return 'publishBid';
            case ManagerBidAction.Cancel:
                return 'cancelBid';
            case ManagerBidAction.ForceStart:
                return 'forceStartBid';
            case ManagerBidAction.ForceClose:
                return 'forceCloseBid';
            default:
                return 'publishBid';
        }
    };

    /**
     * Check if we need to show additional confirmation for workplan manager with past request period
     * @returns {boolean}
     */
    const needsWorkplanPastDateWarning = () => {
        if (!bidProfile || !bidProfile.bidProfileInformation || !bidProfile.schedule) {
            return false;
        }

        const { scheduleSource } = bidProfile.bidProfileInformation;
        const { requestPeriodStartDate } = bidProfile.schedule;
        
        // Only show warning for Workplan Manager schedule source
        if (scheduleSource !== ScheduleSource.WorkplanManager) {
            return false;
        }

        if (!requestPeriodStartDate) {
            return false;
        }
        
        const todayInUkgTimezoneInUserPcTimezone = appliedUkgUserOffSetDateTimeNow(); // value of now in user's UKG timezone but in user's PC timezone
        const requestStartInUserPcTimezone = new Date(requestPeriodStartDate); // requestPeriodStartDate value in user's PC timezone

        return requestStartInUserPcTimezone < todayInUkgTimezoneInUserPcTimezone; // comparison will work because both dates are in user's PC timezone
    };

    /**
     * @param {{value: number}} param0
     */
    const signOffAndPublish = ({ value }) => {
        if (isPublishingToWorkplan || value === ManagerBidAction.RolloutStatusDetails) {
            dispatch(openPublishProgressSidePanel(profileId));
            return;
        }

        // For publish action with workplan manager and past request period, show additional warning
        if (value === ManagerBidAction.Publish && needsWorkplanPastDateWarning()) {
            // @ts-ignore - requestPeriodStartDate is guaranteed to be non-null by needsWorkplanPastDateWarning check
            const requestStartDate = getAsLongDate(bidProfile.schedule.requestPeriodStartDate);

            // Calculate tomorrow's date in UKG user's timezone
            const tomorrow = appliedUkgUserOffSetDateTimeNow();
            tomorrow.setDate(tomorrow.getDate() + 1);
            const tomorrowDate = getAsLongDate(tomorrow);

            toastr.confirm(
                '',
                {
                    // @ts-ignore - component is a valid option for toastr.confirm
                    component: () => (
                        <div style={{ padding: '16px 24px' }}>
                            <Trans
                                i18nKey="shiftBidding:manager.bidProfileEdit.bidDashboard.prompt.publishBidWithPastRequestPeriod"
                                values={{ requestStartDate, tomorrowDate }}
                                components={{ strong: <strong />, br: <br /> }}
                            />
                        </div>
                    ),
                    onOk: () => {
                        toastr.confirm(
                            tPrompt(getPromptKey(value)),
                            {
                                onOk: () => executeAction(value),
                                onCancel: () => { },
                            },
                        );
                    },
                    onCancel: () => { },
                },
            );
        } else {
            toastr.confirm(
                tPrompt(getPromptKey(value)),
                {
                    onOk: () => executeAction(value),
                    onCancel: () => { },
                },
            );
        }
    };
    return signOffAndPublish;
};

export default useBidProfileSplitButton;
